const CatalogElement = globalThis.HTMLElement ?? class {};

function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
  const amount = Number.isFinite(Number(cents)) ? Number(cents) / 100 : 0;
  const [whole, fraction] = amount.toFixed(precision).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return precision === 0 ? grouped : `${grouped}${decimal}${fraction}`;
}

export function normalizeCatalogQuantity(value, { min = 1, max = null, step = 1 } = {}) {
  const safeMin = Math.max(1, Number.parseInt(min, 10) || 1);
  const safeStep = Math.max(1, Number.parseInt(step, 10) || 1);
  const parsedMax = Number.parseInt(max, 10);
  const safeMax = Number.isFinite(parsedMax) ? Math.max(safeMin, parsedMax) : Number.POSITIVE_INFINITY;
  const parsedValue = Number.parseInt(value, 10);
  const safeValue = Number.isFinite(parsedValue) ? parsedValue : safeMin;
  const clamped = Math.min(safeMax, Math.max(safeMin, safeValue));
  const stepped = safeMin + Math.ceil((clamped - safeMin) / safeStep) * safeStep;
  return Math.min(safeMax, stepped);
}

export function calculateCatalogLineTotal(unitPrice, quantity) {
  const safePrice = Math.max(0, Number.parseInt(unitPrice, 10) || 0);
  const safeQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
  return safePrice * safeQuantity;
}

export function formatCatalogMoney(cents, format = '${{amount}}') {
  const plainFormat = format.replace(/<[^>]*>/g, '');
  const token = plainFormat.match(/\{\{\s*([a-z_]+)\s*\}\}/)?.[1];
  if (!token) return formatWithDelimiters(cents);

  const replacements = {
    amount: formatWithDelimiters(cents, 2, ',', '.'),
    amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
    amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
    amount_with_space_separator: formatWithDelimiters(cents, 2, ' ', ','),
    amount_no_decimals_with_space_separator: formatWithDelimiters(cents, 0, ' ', ','),
    amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
  };

  return plainFormat.replace(/\{\{\s*[^}]+\s*\}\}/, replacements[token] ?? replacements.amount);
}

export const CATALOG_OPTION_ORDER = ['item', 'size', 'color', 'pack'];

export function catalogSizeValue(value) {
  return String(value ?? '').trim();
}

function catalogOptionValue(variant, name) {
  if (name === 'size') {
    return catalogSizeValue(variant?.options?.shopifySize)
      || catalogSizeValue(variant?.options?.size);
  }
  return String(variant?.options?.[name] ?? '').trim();
}

function uniqueCatalogValues(variants, name) {
  const values = [];
  const seen = new Set();
  const hasNamedValue = variants.some((variant) => catalogOptionValue(variant, name) !== '');

  variants.forEach((variant) => {
    const value = catalogOptionValue(variant, name);
    if ((!value && !hasNamedValue) || seen.has(value)) return;
    seen.add(value);
    values.push(value);
  });

  return values;
}

function firstPreferredCatalogValue(variants, name, values) {
  const availableValue = values.find((value) =>
    variants.some((variant) => catalogOptionValue(variant, name) === value && variant.available),
  );
  return availableValue ?? values[0] ?? '';
}

export function isCatalogVariantMatrixSafe(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return false;
  const ids = matrix.map((variant) => String(variant?.id ?? ''));
  return ids.every(Boolean) && new Set(ids).size === ids.length;
}

export function resolveCatalogSelection(matrix, currentSelection = {}, change = null) {
  if (!Array.isArray(matrix) || matrix.length === 0) {
    return { selection: {}, optionStates: {}, variant: null };
  }

  const selection = Object.fromEntries(
    CATALOG_OPTION_ORDER.map((name) => [name, String(currentSelection[name] ?? '').trim()]),
  );
  if (change && CATALOG_OPTION_ORDER.includes(change.name)) {
    selection[change.name] = String(change.value ?? '').trim();
  }

  let candidates = [...matrix];
  const optionStates = {};

  CATALOG_OPTION_ORDER.forEach((name) => {
    const values = uniqueCatalogValues(candidates, name);
    const allValues = uniqueCatalogValues(matrix, name);
    optionStates[name] = {
      hidden: allValues.length === 0,
      values: allValues.map((value) => {
        const matching = candidates.filter((variant) => catalogOptionValue(variant, name) === value);
        return {
          value,
          label: value || 'Not specified',
          disabled: matching.length === 0,
          soldOut: matching.length > 0 && matching.every((variant) => !variant.available),
        };
      }),
    };

    if (values.length === 0) {
      selection[name] = '';
      return;
    }

    if (!values.includes(selection[name])) {
      selection[name] = firstPreferredCatalogValue(candidates, name, values);
    }
    candidates = candidates.filter((variant) => catalogOptionValue(variant, name) === selection[name]);
  });

  const selected = candidates.find((candidate) => String(candidate.id) === String(currentSelection.variant))
    ?? candidates.find((candidate) => candidate.available)
    ?? candidates[0];
  if (selected) {
    selection.variant = String(selected.id);
  }

  return { selection, optionStates, variant: selected ?? null };
}

export function selectCatalogVariant(root, variantId) {
  const id = String(variantId);
  const options = [...root.querySelectorAll('[data-catalog-variant-option]')];
  const panels = [...root.querySelectorAll('[data-catalog-variant-panel]')];
  const selectedOption = options.find((option) => option.dataset.variantId === id);
  const selectedPanel = panels.find((panel) => panel.dataset.variantId === id);

  if (!selectedOption || !selectedPanel) return false;

  options.forEach((option) => {
    option.setAttribute('aria-pressed', String(option === selectedOption));
  });
  panels.forEach((panel) => {
    panel.hidden = panel !== selectedPanel;
  });

  const mediaId = selectedOption.dataset.mediaId;
  if (mediaId) selectCatalogMedia(root, mediaId);

  return true;
}

function sizeCatalogGallery(root, media) {
  const gallery = root.querySelector('.catalog-product-gallery');
  const nativeWidth = parseFloat(media?.style?.getPropertyValue?.('--catalog-media-native-width'));
  const nativeHeight = parseFloat(media?.style?.getPropertyValue?.('--catalog-media-native-height'));
  if (!gallery || !nativeWidth || !nativeHeight) return;

  const isMobile = root.ownerDocument?.defaultView?.matchMedia?.('(max-width: 749px)').matches;
  const limit = isMobile ? 300 : 480;
  const availableWidth = gallery.clientWidth || limit;
  const scale = Math.min(1, Math.min(limit, availableWidth) / nativeWidth, limit / nativeHeight);
  gallery.style?.setProperty?.('--catalog-gallery-display-width', `${Math.round(nativeWidth * scale)}px`);
  gallery.style?.setProperty?.('--catalog-gallery-display-height', `${Math.round(nativeHeight * scale)}px`);
}

export function selectCatalogMedia(root, mediaId) {
  const selectedMedia = [...root.querySelectorAll('[data-catalog-media-id]')].find(
    (media) => media.dataset.catalogMediaId === String(mediaId),
  );
  const galleryTrack = root.querySelector('[data-catalog-gallery-track]');
  if (!selectedMedia || !galleryTrack?.scrollTo) return false;

  sizeCatalogGallery(root, selectedMedia);
  const mediaRect = selectedMedia.getBoundingClientRect?.();
  const trackRect = galleryTrack.getBoundingClientRect?.();
  const relativeLeft = mediaRect && trackRect
    ? mediaRect.left - trackRect.left + (galleryTrack.scrollLeft || 0)
    : selectedMedia.offsetLeft;
  const reduceMotion = root.ownerDocument?.defaultView
    ?.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  galleryTrack.scrollTo({ left: relativeLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
  root.querySelectorAll('[data-catalog-thumbnail]').forEach((thumbnail) => {
    thumbnail.setAttribute('aria-current', String(thumbnail.dataset.mediaId === String(mediaId)));
  });
  return true;
}

class LinendipityCatalog extends CatalogElement {
  #abortController = new AbortController();
  #matrix = [];
  #selection = {};
  #configuratorActive = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleVariantSelection, { signal });
    this.querySelector('[data-catalog-gallery-track]')
      ?.addEventListener('scroll', this.#handleGalleryScroll, { signal, passive: true });
    this.querySelector('[data-catalog-gallery-track]')
      ?.addEventListener('scrollend', this.#handleGallerySettled, { signal });
    this.ownerDocument.defaultView?.addEventListener?.('resize', this.#handleGalleryResize, { signal });
    this.#handleGalleryResize();
    this.addEventListener('input', this.#handleQuantityChange, { signal });
    this.addEventListener('change', this.#handleQuantityChange, { signal });
    this.addEventListener('quantity-selector:update', this.#handleQuantityChange, { signal });
    this.#initializeConfigurator();
    this.querySelectorAll('[data-catalog-variant-row]').forEach((row) => this.#updateLineTotal(row));
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleVariantSelection = (event) => {
    const thumbnail = event.target.closest?.('[data-catalog-thumbnail]');
    if (thumbnail) {
      selectCatalogMedia(this, thumbnail.dataset.mediaId);
      return;
    }

    const optionValue = event.target.closest?.('[data-catalog-option-value]');
    if (optionValue && this.#configuratorActive) {
      const optionName = optionValue.dataset.catalogOptionName;
      const result = resolveCatalogSelection(this.#matrix, this.#selection, {
        name: optionName,
        value: optionValue.dataset.catalogOptionValue,
      });
      this.#applyConfiguratorResult(result);
      const selectedControl = this.querySelector(`[data-catalog-option-group="${optionName}"]`)
        ?.querySelector('[data-catalog-option-value][aria-pressed="true"]');
      selectedControl?.focus();
      return;
    }

    const option = event.target.closest?.('[data-catalog-variant-option]');
    if (!option) return;

    if (selectCatalogVariant(this, option.dataset.variantId)) {
      this.#syncVariantSummary(option.dataset.variantId);
      const panel = this.querySelector(`[data-catalog-variant-panel][data-variant-id="${option.dataset.variantId}"]`);
      if (panel) this.#updateLineTotal(panel);
    }
  };

  #handleGalleryScroll = (event) => {
    const track = event.currentTarget;
    const trackLeft = track.getBoundingClientRect().left;
    const media = [...track.querySelectorAll('[data-catalog-media-id]')];
    const nearest = media.reduce((current, candidate) =>
      Math.abs(candidate.getBoundingClientRect().left - trackLeft)
        < Math.abs(current.getBoundingClientRect().left - trackLeft) ? candidate : current,
    media[0]);
    if (!nearest) return;
    this.querySelectorAll('[data-catalog-thumbnail]').forEach((thumbnail) => {
      thumbnail.setAttribute('aria-current', String(thumbnail.dataset.mediaId === nearest.dataset.catalogMediaId));
    });
  };

  #handleGallerySettled = (event) => {
    const activeId = this.querySelector('[data-catalog-thumbnail][aria-current="true"]')?.dataset.mediaId;
    const media = [...event.currentTarget.querySelectorAll('[data-catalog-media-id]')]
      .find((slide) => slide.dataset.catalogMediaId === activeId);
    if (media) sizeCatalogGallery(this, media);
  };

  #handleGalleryResize = () => {
    const activeId = this.querySelector('[data-catalog-thumbnail][aria-current="true"]')?.dataset.mediaId;
    const media = [...this.querySelectorAll('[data-catalog-media-id]')]
      .find((slide) => slide.dataset.catalogMediaId === activeId)
      ?? this.querySelector('[data-catalog-media-id]');
    if (media) sizeCatalogGallery(this, media);
  };

  #handleQuantityChange = (event) => {
    const input = event.target.closest?.('[data-catalog-quantity]')
      ?? event.target.querySelector?.('[data-catalog-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const row = input.closest('[data-catalog-variant-row]');
    if (!row) return;

    const quantity = normalizeCatalogQuantity(input.value, { min: input.min, max: input.max, step: input.step });
    input.value = String(quantity);
    this.#updateLineTotal(row);
  };

  #updateLineTotal(row) {
    const input = row.querySelector('[data-catalog-quantity]');
    const total = row.querySelector('[data-catalog-line-total]');
    if (!(input instanceof HTMLInputElement) || !total) return;

    const quantity = normalizeCatalogQuantity(input.value, { min: input.min, max: input.max, step: input.step });
    const cents = calculateCatalogLineTotal(row.dataset.unitPrice, quantity);
    total.textContent = formatCatalogMoney(cents, this.dataset.moneyFormat);
  }

  #initializeConfigurator() {
    const matrixScript = this.querySelector('[data-catalog-variant-matrix]');
    if (!matrixScript) return;

    try {
      this.#matrix = JSON.parse(matrixScript.textContent || '[]');
    } catch {
      this.#matrix = [];
    }

    if (!isCatalogVariantMatrixSafe(this.#matrix)) return;

    const selectedPanel = [...this.querySelectorAll('[data-catalog-variant-panel]')].find((panel) => !panel.hidden);
    const selectedVariant = this.#matrix.find(
      (variant) => String(variant.id) === String(selectedPanel?.dataset.variantId),
    ) ?? this.#matrix[0];
    if (!selectedVariant) return;

    this.#configuratorActive = true;
    const configurator = this.querySelector('[data-catalog-configurator]');
    const fallback = this.querySelector('[data-catalog-variant-fallback]');
    if (configurator) configurator.hidden = this.#matrix.length === 1;
    if (fallback) fallback.hidden = true;

    const result = resolveCatalogSelection(this.#matrix, {
      ...selectedVariant.options,
      size: catalogOptionValue(selectedVariant, 'size'),
      variant: String(selectedVariant.id),
    }, null);
    this.#applyConfiguratorResult(result);
  }

  #applyConfiguratorResult(result) {
    if (!result?.variant) return;

    this.#selection = result.selection;
    CATALOG_OPTION_ORDER.forEach((name) => {
      const group = this.querySelector(`[data-catalog-option-group="${name}"]`);
      if (!group) return;

      const state = result.optionStates[name];
      group.hidden = state.hidden || (name === 'item' && this.#matrix.length === 1);
      const selectedValue = group.querySelector('[data-catalog-selected-value]');
      if (selectedValue) {
        selectedValue.textContent = result.selection[name] || '';
      }

      const valuesRoot = group.querySelector('[data-catalog-option-values]');
      if (!valuesRoot) return;
      valuesRoot.replaceChildren();

      state.values.forEach(({ value, label, disabled, soldOut }) => {
        const button = this.ownerDocument.createElement('button');
        button.type = 'button';
        button.className = 'catalog-option-value';
        button.textContent = label;
        button.dataset.catalogOptionName = name;
        button.dataset.catalogOptionValue = value;
        button.setAttribute('aria-pressed', String(result.selection[name] === value));
        button.disabled = disabled;
        if (state.values.length === 1) button.dataset.fixed = '';
        if (soldOut) {
          button.dataset.soldOut = '';
          button.setAttribute('aria-label', `${label} — unavailable`);
        }
        valuesRoot.append(button);
      });
    });

    const productSizeInfo = this.querySelector('[data-catalog-product-size-info]');
    if (productSizeInfo) productSizeInfo.hidden = !result.optionStates.size.hidden;

    const configurator = this.querySelector('[data-catalog-configurator]');
    if (configurator) {
      const visibleGroups = [...configurator.querySelectorAll('[data-catalog-option-group]')]
        .filter((group) => !group.hidden);
      configurator.hidden = visibleGroups.length === 0 || this.#matrix.length === 1;
      configurator.dataset.singleAxis = String(visibleGroups.length === 1);
    }

    if (selectCatalogVariant(this, result.variant.id)) {
      this.#syncVariantSummary(result.variant.id);
      const panel = this.querySelector(
        `[data-catalog-variant-panel][data-variant-id="${result.variant.id}"]`,
      );
      if (panel) this.#updateLineTotal(panel);
    }
  }

  #syncVariantSummary(variantId) {
    const variant = this.#matrix.find((entry) => String(entry.id) === String(variantId));
    if (!variant) return;

    const sku = this.querySelector('[data-catalog-selected-sku]');
    const price = this.querySelector('[data-catalog-unit-price]');
    const uom = this.querySelector('[data-catalog-unit-label]');
    if (sku) sku.textContent = variant.sku || '';
    if (price) price.textContent = variant.price || '';
    if (uom) uom.textContent = variant.uom || 'each';
  }
}

if (globalThis.customElements && !customElements.get('linendipity-catalog')) {
  customElements.define('linendipity-catalog', LinendipityCatalog);
}
